import { isMobileDevice } from '@frontend/libs/utils'
import { ArrowRightIcon } from '@heroicons/react/24/solid'
import { Button } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { Link } from 'react-router-dom'

import type {
  DeviceSpecificValue,
  GalleryImage as GalleryImageType,
  GalleryType,
  SeeMoreLink,
} from './lib/type'

import Fancybox from '../fancybox/Fancybox'
import GalleryCarousel from './GalleryCarousel'
import GalleryGrid from './GalleryGrid'
import GalleryImage from './GalleryImage'

export type ImageGalleryProps = {
  columns: DeviceSpecificValue
  enableLightbox: boolean
  firstImageFullWidth: boolean
  galleryType: GalleryType
  gaps: DeviceSpecificValue
  images: GalleryImageType[]
  mobileCarousel: boolean
  seeMoreLink?: SeeMoreLink
  showNavigation?: boolean
  showPagination?: boolean
  title?: string
}

function ImageGallery({
  columns,
  enableLightbox,
  firstImageFullWidth,
  galleryType,
  gaps,
  images,
  mobileCarousel,
  seeMoreLink,
  showNavigation,
  showPagination,
  title,
}: Readonly<ImageGalleryProps>) {
  const renderGalleryGrid = (isLightboxEnabled: boolean) => (
    <GalleryGrid
      $columns={columns}
      $firstImageFullWidth={firstImageFullWidth}
      $gaps={gaps}
    >
      {images.map((image) => (
        <GalleryImage
          image={image}
          isLightboxEnabled={isLightboxEnabled}
          key={image.name}
        />
      ))}
    </GalleryGrid>
  )

  if (galleryType === 'grid') {
    if (isMobileDevice && mobileCarousel) {
      return (
        <GalleryCarousel
          columns={columns}
          enableLightbox={enableLightbox}
          gaps={gaps}
          images={images}
          showNavigation={showNavigation}
          showPagination={showPagination}
        />
      )
    }

    if (enableLightbox) {
      return (
        <Fancybox
          options={{
            Carousel: {
              infinite: false,
            },
          }}
        >
          {renderGalleryGrid(true)}
        </Fancybox>
      )
    }

    return renderGalleryGrid(false)
  }

  return (
    <>
      {title && (
        <h2 className="mb-12 w-full text-center text-2xl font-medium uppercase text-primary">
          {title}
        </h2>
      )}
      <GalleryCarousel
        columns={columns}
        enableLightbox={enableLightbox}
        gaps={gaps}
        images={images}
        showNavigation={showNavigation}
        showPagination={showPagination}
      />
      {seeMoreLink && (
        <div className="my-6 flex justify-center">
          <Button
            as={Link}
            className="mx-auto font-medium uppercase"
            endContent={<ArrowRightIcon className="h-6 w-6" />}
            target={seeMoreLink.target}
            to={seeMoreLink.url}
            variant="light"
          >
            {__('View All', 'asava')}
          </Button>
        </div>
      )}
    </>
  )
}

export default ImageGallery
